import { DP_IDENTIFIER, SEARCH_QUERY_WHITELIST_DP } from '@nykaa/seo/constants';
import { isValidObject } from '@nykaa/utils/validations';
import { ListingFilter } from 'shared/types/filters';
import { filterTypes } from 'shared/constants/filterTypes';
import {
  pageTypes,
  SiteNavigation,
  siteSubNavigationPrefix,
  PageSource,
} from 'shared/constants/dataLayer';
import {
  MULTIPLE_WHITE_SPACES,
  SEARCH_QUERY_WHITELIST,
} from 'shared/constants/regExpression';
import { getErrorString } from 'store/dataLayer/helpers';
import { PageJourney } from 'store/dataLayer/action';
import { ListingProduct } from 'shared/store/categoryListing/types';
import {
  CUSTOMER_NEW_VAL,
  CUSTOMER_REPEAT_VAL,
  CUSTOMER_STATUS,
  ENABLE_INLINE_PRICE_REVEAL,
  HIDDEN_UNDERSCORE,
  INLINE_PRICE_REVEAL_VALUE,
  IS_FILTERED_URL,
  MULTI_TAB_GUIDE,
  PINNED_PRODUCT_ID,
  PLP_WIDGETS,
  SEGMENTED_RANKINGS,
} from 'constants/queryParams';
import { SearchDataLayerData } from './types';
import { FILTER_KEY_REGEX } from 'shared/constants';
import { isMen } from '@nykaa/seo/utils';
import { logger } from '@nykaa/logger';
import { PaidEarlyAccessSegment } from 'shared/store/remoteConfig/types';
import { shouldSendPaidEarlyAccess } from 'shared/helpers/paidEarlyAccess';

export const sanitizeQuery = (
  str: string,
  replaceWith: string = '',
  isDp?: boolean
): string => {
  if (!str || str === '') {
    return str;
  }
  try {
    if (isDp) {
      return str.replace(SEARCH_QUERY_WHITELIST_DP, replaceWith);
    }
    // * Search API allows the following special characters "-" "&" "." "'"
    return str.replace(SEARCH_QUERY_WHITELIST, replaceWith);
  } catch (e) {
    return str;
  }
};

export const filterParamsForSearch = (
  params: any,
  isPro: boolean,
  groupId: string,
  listingPriceRevealEnabled: boolean,
  isNewUser: boolean | null,
  pathname: string,
  priveBand: string,
  multiTabGuide: boolean,
  pinCode: string,
  paidEarlyAccessSegment?: PaidEarlyAccessSegment,
  paidEarlyAccess?: string | null,
  geoLat?: string | null,
  geoLong?: string | null
) => {
  if (!isValidObject(params) && !(pathname?.indexOf(DP_IDENTIFIER) > -1)) {
    throw new Error(`params has to be of type object... passed: ${params}`);
  }

  const { sort = '', q: query = '' } = params;
  const hasExtraPercent = /%(?![0-9A-Fa-f]{2})/.test(query);
  const cleanedQuery = hasExtraPercent
    ? query.replace(/%(?![0-9A-Fa-f]{2})/g, 'percent')
    : query;

  let sanitizedQuery = sanitizeQuery(decodeURIComponent(cleanedQuery), ' ');
  if (pathname?.indexOf(DP_IDENTIFIER) > -1) {
    sanitizedQuery = sanitizeQuery(
      pathname.split(DP_IDENTIFIER)?.[1] || '',
      ' ',
      true
    );
  }

  // ? "red  lipstick" => "red lipstick" transformation is done by logic below
  const sanitizeQueryWithExtraSpacesRemoved = sanitizedQuery
    .replace(MULTIPLE_WHITE_SPACES, ' ')
    .trim();

  const searchParams: UserDefinedObject = {
    ...(sanitizeQueryWithExtraSpacesRemoved && {
      search: sanitizeQueryWithExtraSpacesRemoved,
    }),
    ...(sort && { sort }),
  };

  const parameterKeys = Object.keys(params);

  parameterKeys.forEach((parameterKey: string) => {
    const isOptionalParam =
      parameterKey?.startsWith(HIDDEN_UNDERSCORE) ||
      parameterKey === IS_FILTERED_URL;
    if (isOptionalParam || parameterKey.indexOf('_filter') >= 1) {
      searchParams[parameterKey] = params[parameterKey];
    }
  });

  if (params[PINNED_PRODUCT_ID]) {
    searchParams[PINNED_PRODUCT_ID] = params[PINNED_PRODUCT_ID];
  }

  if (params[SEGMENTED_RANKINGS]) {
    searchParams[SEGMENTED_RANKINGS] = params[SEGMENTED_RANKINGS];
  }

  if (params[PLP_WIDGETS]) {
    searchParams[PLP_WIDGETS] = params[PLP_WIDGETS];
  }
  if (listingPriceRevealEnabled) {
    searchParams[ENABLE_INLINE_PRICE_REVEAL] = INLINE_PRICE_REVEAL_VALUE;
  }
  if (isPro) {
    searchParams.pro = true;
  }
  if (groupId) {
    searchParams.customer_group_id = groupId;
  }
  if (priveBand) {
    searchParams.customer_tier = isMen() ? 'non_prive' : priveBand;
  }
  if (multiTabGuide) {
    searchParams[MULTI_TAB_GUIDE] = multiTabGuide;
  }
  if (isNewUser !== null) {
    if (isNewUser) {
      searchParams[CUSTOMER_STATUS] = CUSTOMER_NEW_VAL;
    } else {
      searchParams[CUSTOMER_STATUS] = CUSTOMER_REPEAT_VAL;
    }
  }
  if (pinCode) {
    searchParams.pinCode = pinCode;
  }
  if (geoLat && geoLong) {
    searchParams.lat = geoLat;
    searchParams.long = geoLong;
  }
  if (shouldSendPaidEarlyAccess(paidEarlyAccessSegment, paidEarlyAccess)) {
    searchParams.paid_early_access = true;
  }
  return searchParams;
};

export const getActiveFilters = (
  requestParams: any,
  filtersFromAPI: ListingFilter[]
) => {
  const activeFilters: { [key: string]: any } = {};

  const filtersInRequest: { [key: string]: any } = {};
  const requestParamKeys = Object.keys(requestParams);

  requestParamKeys.forEach((requestParamKey) => {
    const requestParamValueForKey = requestParams[requestParamKey];
    if (
      requestParamKey &&
      requestParamKey.indexOf('_filter') > -1 &&
      typeof requestParamValueForKey !== 'undefined' &&
      requestParamValueForKey !== null
    ) {
      filtersInRequest[requestParamKey] = requestParamValueForKey;
    }
  });

  if (Array.isArray(filtersFromAPI)) {
    filtersFromAPI.forEach((filter) => {
      const { key, title, type } = filter;

      if (typeof filtersInRequest[key] !== 'undefined') {
        const filterValue = filtersInRequest[key];

        activeFilters[key] = {
          title,
          type,
        };

        switch (type) {
          case filterTypes.SINGLE_SELECT:
            activeFilters[key].data = {
              id: filterValue,
            };
            break;
          case filterTypes.RANGE: {
            const [min, max] = filterValue.split('-');
            activeFilters[key].data = {
              min,
              max,
            };
            break;
          }
          case filterTypes.CATEGORY:
          default:
            activeFilters[key].data = filterValue
              .split(',')
              .map((filterItem: any) => {
                const value = filterItem;
                return {
                  id: value,
                };
              });
            break;
        }
      }
    });
  }

  return activeFilters;
};

export function getDataLayer(data: SearchDataLayerData) {
  const {
    errorCode,
    params,
    suggestionType,
    listingData = {},
    listingPageType = '',
    analyticsSdkData,
  } = data;
  const { isRedirect = false, type: redirectionType = '' } = listingData;
  let redirectionData = {};
  if (isRedirect) {
    redirectionData = {
      redirectionType,
      pageName: 'Search Redirection',
      pageType: pageTypes.SEARCH_REDIRECTION,
    };
  }
  if (errorCode && errorCode > 200) {
    return {
      pageName: getErrorString(errorCode),
      pageType: redirectionData.pageTypes || pageTypes.SEARCH_LISTING,
    };
  }

  let journeyData: PageJourney = {};
  journeyData.siteNavigation = SiteNavigation.SEARCH;
  try {
    journeyData.siteSubNavigation = `${
      siteSubNavigationPrefix.SEARCH
    } ${decodeURI(params.search)}`;
  } catch (error) {
    logger.error('Error in setting siteSubNavigation', error);
  }

  /**
   * eVar2 - previous page name
   * eVar6 - search term - edit page view rule
   * eVar83 - result count - edit page view rule
   */
  const suggestionTypeValue = suggestionType || PageSource.QUERY;
  let listingOpenedFrom = `${PageSource.SEARCH}:${suggestionTypeValue}:${params.search}`;
  if (listingData.totalFound === 0) {
    listingOpenedFrom = `${PageSource.NO_SEARCH_RESULT}:${suggestionTypeValue}:${params.search}`;
  }
  const productIds: string[] = [];
  const brandNamesList: string[] = [];
  let disablePriveProductCount = 0;
  if (Array.isArray(listingData.products)) {
    listingData.products?.forEach((product: ListingProduct) => {
      productIds.push(product?.productId);
      brandNamesList.push(product?.brandName);
      if (product?.disablePriveProduct) {
        disablePriveProductCount++;
      }
    });
  }

  const searchType =
    listingData?.totalFound > 0
      ? listingData?.didYouMean || listingData?.messageForApproxResults
        ? 'alternate'
        : 'query'
      : 'null';
  const selectedFilterKeys = Object.keys(params).filter((key) =>
    FILTER_KEY_REGEX.test(key)
  );

  let filterObj: {}[] = [];
  selectedFilterKeys.forEach((filterKey) => {
    filterObj.push({
      [filterKey]: params[filterKey],
    });
  });

  return {
    pageName: 'Search',
    pageType: pageTypes.SEARCH_LISTING,
    ...journeyData,
    searchListing: {
      resultCount: listingData.totalFound || 0,
      productIds,
      searchTerm: params.search,
      entityCoverage: listingData?.entityCoverage || '',
      entityType: listingData?.entityType || '',
      entityValues: listingData?.entityValues || '',
      brandNamesList,
      disablePriveProductCount,
    },
    searchTerm: params.search,
    totalFound: listingData?.totalFound || 0,
    trackingMetadata: listingData?.trackingMetadata || {},
    didYouMean: listingData.didYouMean || {},
    sortSelection: listingData?.sort || '',
    filters: filterObj,
    listingOpenedFrom,
    searchResultType: searchType,
    guidesAvailable: listingData.guides?.length > 0,
    listingPageType,
    analyticsSdkData,
    ...redirectionData,
  };
}
